import type { StatusKind } from "@/shared/ui/StatusDot";

/**
 * levelTone — the single place that turns a 0–3 hazard level into colour.
 *
 * Before this, every page carried its own `level === 3 ? "red" : …` ladder,
 * which is how a card ended up tinted for "경계" while its badge still said
 * "주의". Route all of it through here.
 *
 * The three colour fields are *token names*, not values, so callers write
 * `var(${tone.soft})` and the four colour-vision palettes plus dark mode
 * keep working — those redefine the tokens, not the mapping.
 *
 * Pairing rule (enforced by scripts/check-theme.mjs):
 *   · `tone` — lines, icons, gauge strokes. Never text on a tinted panel.
 *   · `line` — 보여야 하는 선(위험도 막대 표식·함체 테두리·3D 링). 라이트에서 `tone` 은
 *              흰 바탕 1.5~2.2:1 이라 3:1 을 넘기는 명도 낮춘 짝(2026-09-29). 글자색 금지.
 *   · `soft` — the tinted panel background.
 *   · `fg`   — the ONLY text colour allowed on `soft`.
 */

/** 0 정상 · 1 주의 · 2 경계 · 3 심각 — matches HazardLevel and the RI bands. */
export type Level = 0 | 1 | 2 | 3;

export interface LevelTone {
  level: Level;
  /** StatusDot shape/colour key — carries the signal without colour. */
  kind: StatusKind;
  /** i18n key, shared with StatusDot and HazardLevel so labels never drift. */
  labelKey: string;
  /** Line/icon token, e.g. "--danger". */
  tone: string;
  /** 선·표식 토큰, e.g. "--danger-line" — 다크·색각 팔레트에서는 `tone` 과 같은 색. */
  line: string;
  /** Tinted surface token, e.g. "--danger-soft". */
  soft: string;
  /** Text-on-`soft` token, e.g. "--danger-fg". */
  fg: string;
}

const TONES: readonly LevelTone[] = [
  { level: 0, kind: "ok",       labelKey: "common.status.ok",       tone: "--success", line: "--success-line", soft: "--success-soft", fg: "--success-fg" },
  { level: 1, kind: "caution",  labelKey: "common.status.caution",  tone: "--caution", line: "--caution-line", soft: "--caution-soft", fg: "--caution-fg" },
  { level: 2, kind: "warning",  labelKey: "common.status.warning",  tone: "--warning", line: "--warning-line", soft: "--warning-soft", fg: "--warning-fg" },
  { level: 3, kind: "critical", labelKey: "common.status.critical", tone: "--danger",  line: "--danger-line",  soft: "--danger-soft",  fg: "--danger-fg"  },
] as const;

export function levelTone(level: Level): LevelTone {
  return TONES[level] ?? TONES[0];
}

/**
 * 설비 무응답(health) 경보의 색 — 회색 「설비」 (2026-09-29 결정 11).
 *
 * 이 경보의 등급은 「얼마나 오래 응답이 없나」일 뿐 가스 위험도가 아니다. 등급색을 그대로
 * 쓰면 「현장 PC 응답 없음」이 빨간 「심각」으로 가스 심각과 나란히 서서, 목록을 훑는 눈에
 * 가스 사고처럼 읽힌다. 위험도 「갱신 없음」(멈춘 값)도 이 회색으로 그린다 — 멈춘 값을
 * 정상 초록으로 그리면 매일 밤 LTE 끊김(00:20~05:50) 동안 화면이 「안전」이라고 거짓말한다.
 *
 * 색은 전용 정적 토큰 `--equip*`(color-mix 없음 — WebView 106 안전). 예전의 `--fg-disabled`
 * 는 흰 바탕 1.68:1 이라 통신 이상 아이콘이 거의 안 보였다. 짝 규칙(fg on soft)과 선 대비는
 * check-theme 의 --equip-soft/--equip-fg · --bg(-elevated,-subtle)/--equip 쌍이 지킨다.
 */
export const EQUIPMENT_TONE: LevelTone = {
  level: 0, kind: "offline", labelKey: "pages.alerts.health.badge",
  tone: "--equip", line: "--equip", soft: "--equip-soft", fg: "--equip-fg",
};

/** 경보 한 건의 색 — 설비 무응답이면 회색 「설비」, 아니면 등급색. */
export function alertTone(a: { kind?: string | null; level?: number | null }, comm?: boolean): LevelTone {
  if (comm ?? a.kind === "health") return EQUIPMENT_TONE;
  return levelTone(Math.min(3, Math.max(0, Number(a.level ?? 0))) as Level);
}

/** `var(--danger-soft)` — small helper so call sites stay readable. */
export const cssVar = (token: string) => `var(${token})`;

/**
 * RI (0–1) → level, using the band edges the risk engine itself uses
 * (0.30 / 0.50 / 0.70). Keep in sync with backend
 * safety/risk_engine/engine.py `_CAUTION_RI` / `_WARNING_RI` / `_CRITICAL_RI`.
 */
export function riToLevel(ri: number | null | undefined): Level {
  if (ri == null || Number.isNaN(ri)) return 0;
  if (ri >= 0.7) return 3;
  if (ri >= 0.5) return 2;
  if (ri >= 0.3) return 1;
  return 0;
}

/** RI 등급 구간 하나 — `[from, to)`. 마지막 구간(심각)만 1.0 을 포함한다. */
export interface RiBandDef {
  from: number;
  to: number;
  level: Level;
}

/**
 * RI 등급 구간 네 칸 — 위험도 막대·범례·「주의까지 0.25」가 모두 이 표를 읽는다.
 * 경계값은 `riToLevel` 과 같다(백엔드 engine.py `_CAUTION_RI`·`_WARNING_RI`·`_CRITICAL_RI`).
 * 예전에는 RiskBar 안에 따로 있었다(2026-09-29 이리로 올림).
 */
export const RI_BANDS: readonly RiBandDef[] = [
  { from: 0, to: 0.3, level: 0 },
  { from: 0.3, to: 0.5, level: 1 },
  { from: 0.5, to: 0.7, level: 2 },
  { from: 0.7, to: 1, level: 3 },
];

/**
 * 위험도 프레임이 「지금 값」으로 인정되는 최대 나이(ms) — 백엔드 `RISK_MAX_DATA_AGE_SECONDS`
 * (180초)와 같다. 이보다 오래된 RI 는 **정상 초록으로 그리지 않고** 회색 「갱신 없음」으로
 * 그리며, 현장 최고값 계산에서도 뺀다(매일 밤 LTE 끊김 동안 멈춘 값이 「안전」으로 읽히지 않게).
 */
export const RISK_FRESH_MS = 180_000;

/** `riBand` 결과 — 값이 어느 구간의 어디쯤이고, 다음 등급까지 얼마 남았나. */
export interface RiBand {
  level: Level;
  /** 이 구간의 아래·위 경계. */
  from: number;
  to: number;
  /** 구간 안의 위치 0..1 (구간 막대의 표식 자리). */
  pos: number;
  /** 한 단계 위 등급. 심각이면 null(「최고 구간」). */
  next: Level | null;
  /** 다음 등급 경계까지 남은 RI. 심각이면 null. 표시값(소수 둘째 자리) 기준이며 0 이 되지 않는다. */
  toNext: number | null;
}

/**
 * RI → 구간 풀이. 「정상 0.05」만으로는 경계선까지 얼마나 남았는지 알 수 없어 「주의(0.30)까지
 * 0.25」를 함께 보여 주는 재료다(2026-09-29 관제 개편 A3).
 *
 * `toNext` 는 화면에 찍히는 RI(소수 둘째 자리)에서 뺀다 — 0.127 을 「RI 0.13」으로 찍고
 * 「주의까지 0.18」이라 쓰면 옆의 두 숫자가 서로 안 맞는다. 반올림으로 0 이 되는 경계 직전
 * (0.2999 → 「0.30」)은 등급이 아직 아래이므로 0.01 로 둔다.
 *
 * ⛔ RI 를 퍼센트로 풀지 않는다 — %LEL·%vol 과 헷갈린다.
 */
export function riBand(ri: number): RiBand;
export function riBand(ri: number | null | undefined): RiBand | null;
export function riBand(ri: number | null | undefined): RiBand | null {
  if (ri == null || !Number.isFinite(ri)) return null;
  const v = Math.max(0, Math.min(1, ri));
  const level = riToLevel(v);
  const band = RI_BANDS[level] ?? RI_BANDS[0];
  const span = band.to - band.from;
  const pos = span > 0 ? Math.max(0, Math.min(1, (v - band.from) / span)) : 0;
  const nextBand = level < 3 ? RI_BANDS[level + 1] : undefined;
  let toNext: number | null = null;
  if (nextBand) {
    const shown = Math.round(v * 100) / 100;
    toNext = Math.max(0.01, Math.round((nextBand.from - shown) * 100) / 100);
  }
  return { level, from: band.from, to: band.to, pos, next: nextBand ? nextBand.level : null, toNext };
}
